import React, { FC, useEffect, useState } from 'react';

import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DownloadIconRaw from '@material-ui/icons/GetApp';
import gql from 'graphql-tag';
import { DateTime } from 'luxon';
import { useMutation, useQuery } from 'react-apollo';
import styled from 'styled-components';

import { GeneratePDFReport, GeneratePDFReportVariables, GetAdyenReport } from './__queries__';
import Spinner from './Spinner';

export const REPORTS_QUERY = gql`
  query GetAdyenReport($startDate: String!, $endDate: String!, $legalEntityId: String!) {
    adyenBalanceLegalEntity(id: $legalEntityId) {
      id
      balanceAccounts {
        id
        report(startDate: $startDate, endDate: $endDate) {
          store_id
          date
          amount
          refunds
          fees
        }
      }
    }
  }
`;

const RowContainer = styled.div<{ clickable: boolean }>`
  border-bottom: 1px solid #ddd;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
`;

const RowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  font-size: 1rem;
  color: #333;

  > div {
    display: flex;
    align-items: center;
  }
`;

const DetailsContainer = styled.div`
  background-color: #f7f7f7;
  padding: 16px;
  font-size: 0.875rem;
`;

const StatContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
`;

const StatTitle = styled.span<{ bold?: boolean }>`
  font-weight: ${({ bold }) => (bold ? '600' : 'normal')};
`;

const StatValue = styled.span`
  font-weight: normal;
`;

const DownloadIcon = styled(DownloadIconRaw)`
  height: 24px;
  width: 24px;
  margin-left: 8px;
  cursor: pointer;
  color: #14b8a6;
`;

const MonthName = styled.span`
  font-weight: 600;
`;

interface StatProps {
  title: string;
  value: number;
  bold?: boolean;
  prefix?: string;
}

const Stat: FC<StatProps> = ({ title, value, bold = false, prefix = '' }) => (
  <StatContainer>
    <StatTitle bold={bold}>{title}:</StatTitle>
    <StatValue>
      {prefix} {(value / 100).toLocaleString()}
    </StatValue>
  </StatContainer>
);

export const GENERATE_REPORT = gql`
  mutation GeneratePDFReport($data: AdyenPDFReportInput!) {
    generateAdyenPDFReport(data: $data) {
      success
      message
      error
    }
  }
`;

interface MonthRowProps {
  month: { name: string; index: number };
  year: number;
  startDate: string;
  endDate: string;
  onClick: (reportData: any) => void;
  isFirst?: boolean;
  legalEntityId: string;
  balanceAccountId: string;
}

const MonthRow: FC<MonthRowProps> = ({
  month,
  startDate,
  endDate,
  onClick,
  isFirst,
  legalEntityId,
  balanceAccountId,
}) => {
  const [loadingPDFReport, setLoadingPDFReport] = useState(false);
  const [isOpen, setIsOpen] = useState(!!isFirst);

  const [generatePdfReport] = useMutation<GeneratePDFReport, GeneratePDFReportVariables>(
    GENERATE_REPORT,
  );

  const reportsQuery = useQuery<GetAdyenReport>(REPORTS_QUERY, {
    variables: { startDate, endDate, legalEntityId: legalEntityId },
    fetchPolicy: 'network-only',
    skip: !legalEntityId,
  });

  const reports = reportsQuery.data?.adyenBalanceLegalEntity?.balanceAccounts[0]?.report || [];
  const report = reports[0];

  useEffect(() => {
    if (!reports?.length || !isFirst) return;
    onClick(reports);
  }, [reports]);

  const handleClick = () => {
    setIsOpen(!isOpen);
    onClick(isOpen ? [] : reports || []);
  };

  const handleReportDownload = async () => {
    setLoadingPDFReport(true);

    try {
      const result = await generatePdfReport({
        variables: {
          data: {
            startDate,
            endDate,
            balanceAccountId,
          },
        },
      });
      setLoadingPDFReport(false);

      const success = result.data?.generateAdyenPDFReport.success;
      const message = result.data?.generateAdyenPDFReport.message;

      if (!success) throw new Error('Failed to generate PDF report');

      const date = DateTime.fromISO(startDate).toFormat('yyyy_MM');
      const a = document.createElement('a');
      a.href = `data:application/pdf;base64,${message}`;
      a.download = `Baemingo_Monthly_Fee_Statement_${date}.pdf`;
      a.click();
    } catch (error) {
      setLoadingPDFReport(false);
    }
  };

  return (
    <RowContainer clickable={!!report}>
      {!reportsQuery.error && !report && (
        <RowHeader>
          <div>{month.name}</div>
          {reportsQuery.loading ? <Spinner /> : <span>No sales</span>}
        </RowHeader>
      )}

      {report && (
        <RowHeader onClick={handleClick}>
          <div>
            {isOpen ? (
              <ExpandMoreIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
            ) : (
              <ChevronRightIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
            )}
            <MonthName>{month.name}</MonthName>
          </div>
          {loadingPDFReport ? (
            <Spinner />
          ) : (
            <DownloadIcon
              onClick={(e) => {
                e.stopPropagation();
                handleReportDownload();
              }}
            />
          )}
        </RowHeader>
      )}

      {report && isOpen && (
        <DetailsContainer>
          {reportsQuery.error && <div>Error loading data for {month.name}</div>}
          {!reportsQuery.error && (
            <>
              <Stat title="Amount" value={report.amount} />
              <Stat title="Refunds" value={report.refunds} prefix="-" />
              <hr />
              <Stat bold title="After Refunds" value={report.amount - report.refunds} prefix="=" />
              <Stat title="Fees" value={report.fees} prefix="-" />
              <hr />
              <Stat
                bold
                title="Net Result"
                value={report.amount - report.refunds - report.fees}
                prefix="="
              />
            </>
          )}
        </DetailsContainer>
      )}
    </RowContainer>
  );
};

export default MonthRow;
